import Modal from "@mui/material/Modal";
import ButtonCloseModal from "../../Buttons/ButtonCloseModal/ButtonCloseModal";

interface PatientProgressHelpModalProps {
  onClose: () => void;
}

const PatientProgressHelpModal = ({
  onClose,
}: PatientProgressHelpModalProps) => {
  return (
    <Modal open onClose={onClose}>
      <div className="w-[90%] max-w-[800px] max-h-[90vh] py-5 px-3 flex flex-col absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-md border border-pelorous md:py-7 md:px-10 overflow-auto">
        <div className="flex justify-between items-start mb-5">
          <h2 className="text-xl leading-6 text-cloudBurst font-bold">
            Patient Progress Help
          </h2>
          <ButtonCloseModal onClick={onClose} />
        </div>

        <div className="flex flex-col gap-6 text-cloudBurst">
          {/* FILTERS Section */}
          <div>
            <h3 className="text-lg font-bold mb-3 uppercase">FILTERS</h3>

            <div className="flex flex-col gap-4">
              {/* General */}
              <div>
                <h4 className="font-bold mb-1">General</h4>
                <p className="text-sm leading-relaxed">
                  The filtering options allow users to quickly isolate, analyze,
                  and view specific patient data subsets. Please note, the
                  metric results provided include any tests performed via In
                  Clinic, At Home or Remote Assist platforms, regardless of
                  application-specific filters selected.
                </p>
              </div>

              {/* Search */}
              <div>
                <h4 className="font-bold mb-1">Search</h4>
                <p className="text-sm leading-relaxed">
                  Search by Patient Name or Patient ID.
                </p>
              </div>

              {/* Reset */}
              <div>
                <h4 className="font-bold mb-1">Reset</h4>
                <p className="text-sm leading-relaxed">
                  Resets filters to default.
                </p>
              </div>

              {/* Date Range */}
              <div>
                <h4 className="font-bold mb-1">Date Range</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Select the date range for comparison purposes. Calendar
                  filters include:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>
                    <strong>All:</strong> Compares average score for first test
                    day and most recent test day.
                  </li>
                  <li>
                    <strong>Last 2 test days:</strong> Compares average score
                    for most recent two days of testing.
                  </li>
                  <li>
                    <strong>Last 30 days:</strong> Compares average score for
                    first and last test days within the past 30 days.
                  </li>
                  <li>
                    <strong>Last 60 days:</strong> Compares average score for
                    first and last test days within the past 60 days.
                  </li>
                  <li>
                    <strong>Last 90 days:</strong> Compares average score for
                    first and last test days within the past 90 days.
                  </li>
                </ul>
              </div>

              {/* Order Type */}
              <div>
                <h4 className="font-bold mb-1">Order Type</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Review a list of orders based on their completion status:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>
                    <strong>All:</strong> Displays In Clinic patients and RKM
                    (current, expired and canceled) patients.
                  </li>
                  <li>
                    <strong>In Clinic Only Patients:</strong> Displays patients
                    tested via In Clinic that have never had RKM testing.
                  </li>
                  <li>
                    <strong>RKM Patients:</strong> Displays patients with
                    current, expired and canceled order status.
                  </li>
                  <li>
                    <strong>Current RKM Patients:</strong> Displays patients
                    with current RKM orders only.
                  </li>
                  <li>
                    <strong>Expired/Canceled RKM Patients:</strong> Displays
                    patients with expired RKM orders only.
                  </li>
                </ul>
                <p className="text-sm leading-relaxed mt-2 italic">
                  *Patients that have been converted to "Inactive" are excluded.
                  For inclusion, they must be converted back to "Active" via the
                  Patients tab &gt; select inactive &gt; locate patient &gt;
                  select more options &gt; Activate.
                </p>
              </div>

              {/* Record Review Status */}
              <div>
                <h4 className="font-bold mb-1">Record Review Status</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Review orders by patients' record review status:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>
                    <strong>All:</strong> Displays all patients regardless of
                    when their records were last accessed.
                  </li>
                  <li>
                    <strong>Not Accessed Past 30 days:</strong> Displays only
                    patient with records that have not been opened by any
                    account user in the past 30 days. Select the associated
                    "View Patient" link to open the patient's dashboard.
                  </li>
                </ul>
              </div>

              {/* Ordering Clinician */}
              <div>
                <h4 className="font-bold mb-1">Ordering Clinician</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Review orders by the assigned Ordering Clinician:
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>
                    <strong>All:</strong> Display orders for all ordering
                    clinicians within the account.
                  </li>
                  <li>
                    <strong>Last Name, First Name:</strong> Display orders
                    assigned to a specific ordering clinician.
                  </li>
                </ul>
              </div>

              {/* Tests Run By */}
              <div>
                <h4 className="font-bold mb-1">Tests Run By</h4>
                <p className="text-sm leading-relaxed mb-2">
                  Review orders by the account users/admins that perform patient
                  testing.
                </p>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  <li>
                    <strong>ALL:</strong> Display all self-tested patients and
                    patients ever tested by any user/admin within the account.
                  </li>
                  <li>
                    <strong>Last Name, First Name:</strong> Display all patients
                    ever tested by a specific user/admin within the account.
                  </li>
                </ul>
                <p className="text-sm leading-relaxed mt-2 italic">
                  *User/admins that have been "deactivated" are excluded. For
                  inclusion, they must be converted back to "Active" via the
                  Admin tab &gt; select inactive &gt; locate user &gt; select
                  more options &gt; Activate.
                </p>
              </div>
            </div>
          </div>

          {/* COLUMN DETAILS Section */}
          <div>
            <h3 className="text-lg font-bold mb-3 uppercase">
              COLUMN DETAILS
            </h3>

            <div className="flex flex-col gap-4">
              {/* Date Range */}
              <div>
                <h4 className="font-bold mb-1">Date Range</h4>
                <p className="text-sm leading-relaxed">
                  Identifies the first and last testing dates occurring within a
                  specified Date Range filter. Example, if on January 31, 2026,
                  a user selected the Last 30 days filter, and the patient only
                  tested on January 15, 01/15/2026 - 01/15/2026 would be
                  displayed in the column.
                </p>
              </div>

              {/* Balance */}
              <div>
                <h4 className="font-bold mb-1">Balance</h4>
                <p className="text-sm leading-relaxed">
                  Quickly compare patient progress over a selected timeframe.
                  The calculation compares the cumulative average score for all
                  balance test options on the first day of testing against the
                  last date of testing within the selected date range.
                </p>
              </div>

              {/* Gait */}
              <div>
                <h4 className="font-bold mb-1">Gait</h4>
                <p className="text-sm leading-relaxed">
                  Quickly compare patient progress over a selected timeframe.
                  The calculation compares the cumulative average score for all
                  gait tests on the first day of testing against the last date
                  of testing within the selected date range.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PatientProgressHelpModal;
